import { useEffect, useRef, useState, type JSX } from 'react';
import { useGlassHover } from '../lib/useGlassHover';
import { fmtTime } from '../lib/useAudio';

export interface NowPlayingCardProps {
  title: string;
  artist: string;
  videoId?: string | null;
  playing: boolean;
  blocked: boolean;
  muted: boolean;
  connected: boolean;
  offline?: boolean;
  fetching: boolean;
  buffering: boolean;
  picking: boolean;
  modePreparing: boolean;
  volume: number;
  currentTime: number;
  duration: number;
  onToggle(): void;
  onPrev(): void;
  onNext(): void;
  onVolume(v: number): void;
  onSeek(t: number): void;
  liked: boolean;
  onStamp(): void;
  waveform: React.ReactNode;
  lyrics: React.ReactNode;
}

function PrevIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
      <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
    </svg>
  );
}

function NextIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
      <path d="M16 6h2v12h-2zm-3.5 6l-8.5 6V6z" />
    </svg>
  );
}

function PlayIcon({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
      <path d="M8 5.5v13l11-6.5z" />
    </svg>
  );
}

function PauseIcon({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
      <rect x="6" y="5" width="4" height="14" />
      <rect x="14" y="5" width="4" height="14" />
    </svg>
  );
}

export function NowPlayingCard(props: NowPlayingCardProps): JSX.Element {
  const {
    title,
    artist,
    videoId,
    playing,
    blocked,
    muted,
    connected,
    offline,
    fetching,
    buffering,
    picking,
    modePreparing,
    volume,
    currentTime,
    duration,
    onToggle,
    onPrev,
    onNext,
    onVolume,
    onSeek,
    liked,
    onStamp,
    waveform,
    lyrics,
  } = props;

  const hoverRef = useGlassHover<HTMLDivElement>();
  const [stamping, setStamping] = useState(false);
  const stampTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [thumbFailed, setThumbFailed] = useState(false);
  useEffect(() => setThumbFailed(false), [videoId]);
  const playRef = useRef<HTMLButtonElement>(null);
  const [ruler, setRuler] = useState(false);
  const rulerTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const volumeRef = useRef(volume);
  volumeRef.current = volume;
  const onVolumeRef = useRef(onVolume);
  onVolumeRef.current = onVolume;

  useEffect(() => {
    const el = playRef.current;
    if (!el) return;
    const h = (e: WheelEvent) => {
      e.preventDefault();
      const step = e.deltaY < 0 ? 0.04 : -0.04;
      onVolumeRef.current(Math.max(0, Math.min(1, volumeRef.current + step)));
      setRuler(true);
      if (rulerTimer.current) clearTimeout(rulerTimer.current);
      rulerTimer.current = setTimeout(() => setRuler(false), 900);
    };
    el.addEventListener('wheel', h, { passive: false });
    return () => el.removeEventListener('wheel', h);
  }, []);

  useEffect(() => {
    return () => {
      if (rulerTimer.current) clearTimeout(rulerTimer.current);
      if (stampTimer.current) clearTimeout(stampTimer.current);
    };
  }, []);

  const stamp = (e: React.MouseEvent<HTMLButtonElement>) => {
    onStamp();
    setStamping(true);
    if (stampTimer.current) clearTimeout(stampTimer.current);
    stampTimer.current = setTimeout(() => setStamping(false), 300);
    e.currentTarget.blur();
  };

  const statusText = !connected
    ? offline
      ? '没网了，先放本机存好的歌'
      : '失联了，重连中…'
    : modePreparing
      ? '新模式的歌准备中…'
    : muted
      ? '电台在播 · 点一下收听'
      : fetching
        ? '正在取这首歌，马上就来…'
        : buffering
          ? '缓冲中…'
          : blocked
            ? '点一下，电台开始'
            : picking
              ? 'DJ 在挑歌…'
              : '';
  const waiting = fetching || buffering || picking || modePreparing;

  const showPlay = !playing || blocked || muted;
  const pct = duration > 0 ? Math.max(0, Math.min(100, (currentTime / duration) * 100)) : 0;

  const click = (fn: () => void) => (e: React.MouseEvent<HTMLButtonElement>) => {
    fn();
    e.currentTarget.blur();
  };

  return (
    <div
      ref={hoverRef}
      className="glass glass-i w-[min(92vw,26rem)] rounded-[28px] px-6 pt-6 pb-4 select-none"
    >
      <div className="flex items-center gap-4">
        {videoId && !thumbFailed && (
          <img
            key={videoId}
            src={`https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`}
            alt=""
            className="h-14 w-14 shrink-0 rounded-xl border object-cover"
            style={{ borderColor: 'var(--glass-line)', boxShadow: 'var(--glass-shadow)' }}
            onError={() => setThumbFailed(true)}
          />
        )}
        <div className="min-w-0 flex-1">
          <h1
            className="font-display break-words font-semibold text-fg"
            style={{
              fontSize: 'clamp(1.3rem,3.5vw,1.9rem)',
              lineHeight: 1.15,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {title || 'Claudio'}
          </h1>
          <div className="mt-1 truncate text-base text-fg/60">{artist || 'your personal AI DJ'}</div>
        </div>
        {!!title && (
          <button
            aria-label={liked ? '取消收藏' : '收藏这首歌'}
            title={liked ? '已藏' : '收藏'}
            onClick={stamp}
            className="-m-2 shrink-0 cursor-pointer self-start p-2 outline-none"
          >
            <span className={`collect-seal ${liked ? 'is-liked' : ''} ${stamping ? 'stamp-in' : ''}`}>藏</span>
          </button>
        )}
      </div>

      <div className="mt-4 flex h-14 items-center justify-center">{waveform}</div>

      <div className="mt-3 flex items-center justify-center gap-4">
        <button
          aria-label="上一首"
          onClick={click(onPrev)}
          className="glass grid h-11 w-11 cursor-pointer place-items-center rounded-full text-fg outline-none transition active:scale-95"
        >
          <PrevIcon />
        </button>

        <div className="relative">
          <button
            ref={playRef}
            aria-label={playing ? '暂停' : '播放'}
            onClick={click(onToggle)}
            className={`${connected ? 'glass' : ''} grid h-14 w-14 cursor-pointer place-items-center rounded-full text-fg outline-none transition active:scale-95`}
            style={
              connected
                ? undefined
                : {
                    background: 'transparent',
                    boxShadow: 'inset 0 0 0 1.5px color-mix(in srgb, var(--fg) 60%, transparent)',
                  }
            }
          >
            {showPlay ? <PlayIcon className="h-6 w-6" /> : <PauseIcon className="h-6 w-6" />}
          </button>
          <div
            className="pointer-events-none absolute left-full top-1/2 ml-2 flex -translate-y-1/2 flex-col-reverse gap-[3px]"
            style={{ opacity: ruler ? 1 : 0, transition: 'opacity 150ms' }}
          >
            {Array.from({ length: 11 }, (_, i) => (
              <span key={i} className={`h-px w-3 ${i / 10 <= volume ? 'bg-fg/70' : 'bg-fg/20'}`} />
            ))}
          </div>
        </div>

        <button
          aria-label="下一首"
          onClick={click(onNext)}
          className="glass grid h-11 w-11 cursor-pointer place-items-center rounded-full text-fg outline-none transition active:scale-95"
        >
          <NextIcon />
        </button>
      </div>

      <div className={`mt-2 h-5 truncate text-center text-xs text-muted ${waiting ? 'animate-pulse' : ''}`}>
        {statusText}
      </div>

      <div className="mt-2 min-h-[1.5rem]">{lyrics}</div>

      <div
        className="mt-3 flex h-5 items-center gap-2"
      >
        {duration > 0 && (
          <>
            <span className="shrink-0 text-[10px] tabular-nums text-faint">{fmtTime(currentTime)}</span>
            <div
              className="relative h-full flex-1 cursor-pointer"
              onPointerDown={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                onSeek(Math.max(0, Math.min(duration, ((e.clientX - r.left) / r.width) * duration)));
              }}
            >
              <div className="absolute top-1/2 h-px w-full" style={{ background: 'var(--line)' }} />
              <div
                className="absolute top-1/2 h-px"
                style={{ width: `${pct}%`, background: 'color-mix(in srgb, var(--fg) 50%, transparent)' }}
              />
            </div>
            <span className="shrink-0 text-[10px] tabular-nums text-faint">{fmtTime(duration)}</span>
          </>
        )}
      </div>
    </div>
  );
}
